import React, { useEffect, useState, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ChevronRight, ChevronLeft } from "lucide-react";
import api from "../lib/api";
import { useTranslation } from "react-i18next";

/**
 * CreatorTour – spotlight + boble onboarding for nye skapere.
 *
 * Bruk:
 *   <CreatorTour steps={STEPS} onClose={() => setOpen(false)} />
 *
 * Hvert steg har: {key, title, body, targetSelector, placement?}
 *   - targetSelector: CSS-selector som lyses opp (eks. '[data-tour="banner"]')
 *   - placement: 'bottom' | 'top' | 'left' | 'right' (default 'bottom')
 *   - Hvis targetSelector er null → vises midt på skjermen som en intro/outro-modal.
 */
export default function CreatorTour({ steps = [], onClose, onComplete, onStepChange, completeEndpoint = "/me/creator-tour-completed" }) {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const overlayRef = useRef(null);

  const step = steps[idx];

  // Beregn posisjon for hver step
  const computeRect = () => {
    if (!step) return;
    if (!step.targetSelector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(step.targetSelector);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      // Scroll til element hvis utenfor view
      const inView = r.top >= 80 && r.bottom <= window.innerHeight - 80;
      if (!inView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // re-compute etter scroll
        setTimeout(() => {
          const r2 = el.getBoundingClientRect();
          setRect({ top: r2.top, left: r2.left, width: r2.width, height: r2.height });
        }, 350);
      }
    } else {
      setRect(null);
    }
  };

  useLayoutEffect(() => {
    computeRect();
    onStepChange?.(steps[idx], idx);
    const onResize = () => computeRect();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  const next = () => {
    if (idx < steps.length - 1) setIdx(idx + 1);
    else finish();
  };
  const prev = () => idx > 0 && setIdx(idx - 1);
  const finish = async () => {
    try { await api.post(completeEndpoint); } catch (e) { /* ignore */ }
    onComplete?.();
    onClose?.();
  };
  const skip = async () => {
    try { await api.post(completeEndpoint); } catch (e) { /* ignore */ }
    onClose?.();
  };

  if (!step) return null;

  // Posisjoner bobla
  const bubble = computeBubblePosition(rect, step.placement || "bottom");

  return createPortal(
    <div ref={overlayRef} className="fixed inset-0 z-[9999]" data-testid="creator-tour">
      {/* Bakgrunnsmasking med hull */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - 8}
                y={rect.top - 8}
                width={rect.width + 16}
                height={rect.height + 16}
                rx="6"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(10,10,10,0.82)" mask="url(#tour-mask)" />
      </svg>

      {/* Highlight-ramme rundt elementet */}
      {rect && (
        <div
          className="absolute pointer-events-none border-2 border-[#D11A5B]"
          style={{
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16,
            boxShadow: "0 0 0 4px rgba(209,26,91,0.18), 0 0 40px rgba(209,26,91,0.35)",
            borderRadius: 6,
            transition: "all 0.3s ease-out",
          }}
        />
      )}

      {/* Boble */}
      <div
        className="absolute bg-[#0F0F0F] border border-[#D11A5B]/40 shadow-2xl max-w-sm p-5"
        style={{
          top: bubble.top,
          left: bubble.left,
          transform: bubble.transform,
          transition: "all 0.3s ease-out",
        }}
        data-testid={`tour-step-${step.key}`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[#D11A5B]">
            {t("beta_tour.step", "Steg").toUpperCase()} {idx + 1} / {steps.length}
          </div>
          <button
            onClick={skip}
            className="text-[#ADA59A] hover:text-[#F2EBE0]"
            data-testid="tour-close"
            aria-label={t("beta_tour.close", "Lukk omvisning")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="font-fraunces italic text-2xl text-[#F2EBE0] mb-2 leading-tight">
          {step.title}
        </h3>
        <div className="text-sm text-[#ADA59A] leading-relaxed mb-5 whitespace-pre-line">
          {step.body}
        </div>
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={skip}
            className="text-xs font-meta tracking-[0.15em] text-[#ADA59A] hover:text-[#F2EBE0]"
            data-testid="tour-skip"
          >
            {t("beta_tour.skip", "Hopp over").toUpperCase()}
          </button>
          <div className="flex items-center gap-1">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1 transition-all ${
                  i === idx ? "w-6 bg-[#D11A5B]" : i < idx ? "w-3 bg-[#D11A5B]/40" : "w-3 bg-white/10"
                }`}
              />
            ))}
          </div>
          <div className="flex gap-1">
            {idx > 0 && (
              <button
                onClick={prev}
                className="px-3 py-1.5 text-xs font-meta tracking-[0.15em] border border-white/20 text-[#F2EBE0] hover:border-[#D11A5B]"
                data-testid="tour-prev"
              >
                <ChevronLeft className="h-3 w-3 inline" /> {t("beta_tour.back", "Forrige").toUpperCase()}
              </button>
            )}
            <button
              onClick={next}
              className="px-3 py-1.5 text-xs font-meta tracking-[0.15em] bg-[#D11A5B] text-white hover:opacity-90"
              data-testid="tour-next"
            >
              {idx === steps.length - 1 ? t("beta_tour.last", "Ferdig").toUpperCase() : <>{t("beta_tour.next", "Neste").toUpperCase()} <ChevronRight className="h-3 w-3 inline" /></>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function computeBubblePosition(rect, placement) {
  const BUBBLE_W = 384;
  const BUBBLE_H = 240;
  const GAP = 20;
  const winW = typeof window !== "undefined" ? window.innerWidth : 1200;
  const winH = typeof window !== "undefined" ? window.innerHeight : 800;

  if (!rect) {
    // Senter modal hvis ingen target
    return {
      top: winH / 2,
      left: winW / 2,
      transform: "translate(-50%, -50%)",
    };
  }

  let top, left, transform;
  switch (placement) {
    case "top":
      top = rect.top - GAP;
      left = rect.left + rect.width / 2;
      transform = "translate(-50%, -100%)";
      break;
    case "left":
      top = rect.top + rect.height / 2;
      left = rect.left - GAP;
      transform = "translate(-100%, -50%)";
      break;
    case "right":
      top = rect.top + rect.height / 2;
      left = rect.left + rect.width + GAP;
      transform = "translate(0, -50%)";
      break;
    case "bottom":
    default: {
      top = rect.top + rect.height + GAP;
      left = rect.left + rect.width / 2;
      transform = "translate(-50%, 0)";
      // Hvis bobla ville gått ut av skjermen nedenfor, flytt opp
      if (top + BUBBLE_H > winH - 20) {
        top = rect.top - GAP;
        transform = "translate(-50%, -100%)";
      }
      break;
    }
  }

  // Universal edge-clamping — sikrer at bobla alltid er innenfor viewport
  // uansett placement. Justerer transform til å bruke absolutt posisjon når
  // vi må klemme mot kanten.
  const halfW = BUBBLE_W / 2;
  const isCentered = transform.includes("-50%");
  if (isCentered) {
    if (left - halfW < 20) left = 20 + halfW;
    if (left + halfW > winW - 20) left = winW - 20 - halfW;
  } else if (transform.includes("-100%, ")) {
    // left placement: sørg for at bobla ikke går ut til venstre
    if (left - BUBBLE_W < 20) left = 20 + BUBBLE_W;
  } else {
    // right placement: sørg for at bobla ikke går ut til høyre
    if (left + BUBBLE_W > winW - 20) left = winW - 20 - BUBBLE_W;
  }
  // Vertikal clamping
  if (transform.includes(", -100%")) {
    if (top - BUBBLE_H < 20) top = 20 + BUBBLE_H;
  } else if (transform.includes(", 0)") || transform.includes(", -50%")) {
    if (top + BUBBLE_H > winH - 20) top = winH - 20 - BUBBLE_H;
    if (top < 20) top = 20;
  }

  return { top, left, transform };
}
